import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import DetalleReservaModal from "../modal/DetalleReservaModal.jsx";
import EstadoReserva from "./EstadoReserva.jsx";

const apiUrl = process.env.REACT_APP_API_RESERVAS;
const apiUrlId = process.env.REACT_APP_API_RESERVAS_ID;
const apiUrlModificar = process.env.REACT_APP_API_RESERVAS_MODIFICACIONES;

function ReservasVisualizador() {
  const [reservas, setReservas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReserva, setSelectedReserva] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reservaToDelete, setReservaToDelete] = useState(null);
  //const [usuarioCreadorNombre, setUsuarioCreadorNombre] = useState({}); // Ahora es un objeto para cada reserva
  const [filtroReservaId, setFiltroReservaId] = useState(""); // Filtro por N° de reserva
  const [filtroFecha, setFiltroFecha] = useState(""); // Filtro por Fecha de creación
  const [ultimasReservasIds, setUltimasReservasIds] = useState([]);

  useEffect(() => {
    const fetchReservas = async () => {
      try {
        const response = await axios.get(apiUrl);
        const reservasOrdenadas = response.data.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );

        // Obtener los IDs de las últimas 5
        const ultimas = reservasOrdenadas.slice(0, 5).map((r) => r.id);
        setUltimasReservasIds(ultimas);

        setReservas(reservasOrdenadas);
      } catch (error) {
        console.error("Error al obtener reservas:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchReservas();
  }, []);

  useEffect(() => {
    const calcularContadores = async () => {
      const resumen = {
        Completa: 0,
        Parcial: 0,
        Vacía: 0,
        "Sin miembros": 0,
        Error: 0,
      };

      for (const reserva of reservas) {
        try {
          const res = await axios.get(`${apiUrlId}${reserva.id}`);
          const miembros = res.data.miembros || [];
          const total = miembros.length;
          const conHabitacion = miembros.filter(
            (m) => m.habitacion_asignada
          ).length;
          const tieneReservaQlo =
            res.data.reserva_qloapp || miembros[0]?.reserva_qloapp;

          if (total === 0) resumen["Sin miembros"]++;
          else if (conHabitacion === total && tieneReservaQlo)
            resumen.Completa++;
          else if (conHabitacion > 0 || tieneReservaQlo) resumen.Parcial++;
          else resumen.Vacía++;
        } catch (err) {
          resumen.Error++;
        }
      }

      setContadorEstados(resumen);
    };

    if (reservas.length > 0) {
      calcularContadores();
    }
  }, [reservas]);

  const [contadorEstados, setContadorEstados] = useState({
    Completa: 0,
    Parcial: 0,
    Vacía: 0,
    "Sin miembros": 0,
    Error: 0,
  });

  const handleRowClick = (reserva) => {
    setSelectedReserva(reserva);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedReserva(null);
  };

  const handleOpenDeleteDialog = (reserva) => {
    setReservaToDelete(reserva);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setReservaToDelete(null);
  };

  const handleDeleteReserva = async () => {
    try {
      await axios.delete(`${apiUrlModificar}/${reservaToDelete.id}`);
      setReservas(
        reservas.filter((reserva) => reserva.id !== reservaToDelete.id)
      );
      handleCloseDeleteDialog();
    } catch (error) {
      console.error("Error al eliminar la reserva:", error);
    }
  };

  const reservasFiltradas = reservas.filter((reserva) => {
    const coincideId = filtroReservaId
      ? reserva.id.toString().includes(filtroReservaId)
      : true;
    const coincideFecha = filtroFecha
      ? reserva.createdAt.startsWith(filtroFecha)
      : true;
    return coincideId && coincideFecha;
  });

  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: "2rem" }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Cargando reservas...
        </Typography>
      </div>
    );
  }
  return (
    <>
      <Grid
        container
        alignItems="flex-start"
        justifyContent="space-between"
        sx={{ mb: 2, px: 2 }}
      >
        {/* 🧭 Filtros a la izquierda */}
        <Grid item xs={12} md={7}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Buscar por N° de Reserva"
                variant="outlined"
                value={filtroReservaId}
                onChange={(e) => setFiltroReservaId(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Buscar por Fecha de Creación"
                type="date"
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                value={filtroFecha}
                onChange={(e) => setFiltroFecha(e.target.value)}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* 📊 Contadores a la derecha, bien pegados */}
        <Grid item xs={12} md="auto">
          <Grid container spacing={1} wrap="nowrap">
            {[
              { label: "Completa", color: "#c8e6c9", text: "#2e7d32" },
              { label: "Parcial", color: "#fff9c4", text: "#f57f17" },
              { label: "Vacía", color: "#ffcdd2", text: "#c62828" },
            ].map(({ label, color, text }) => (
              <Grid item key={label}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 1.5,
                    minWidth: 100,
                    textAlign: "center",
                    backgroundColor: color,
                    whiteSpace: "nowrap",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ color: text, fontWeight: "bold" }}
                  >
                    {label}
                  </Typography>
                  <Typography variant="h6" sx={{ color: text }}>
                    {contadorEstados[label] ?? 0}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>

      {/* Tabla de Reservas */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Estado de carga</strong>
              </TableCell>
              <TableCell>ID de la reserva</TableCell>
              <TableCell>Fecha de creación</TableCell>
              <TableCell>Autorizador de reserva</TableCell>
              <TableCell>Responsable</TableCell>
              <TableCell>Fecha Entrada</TableCell>
              <TableCell>Fecha Salida</TableCell>
              <TableCell>Archivo</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reservasFiltradas.map((reserva) => (
              <TableRow
                key={reserva.id}
                hover
                onClick={() => handleRowClick(reserva)}
                style={{
                  cursor: "pointer",
                  backgroundColor: ultimasReservasIds.includes(reserva.id)
                    ? "#e3f2fd"
                    : "inherit", // celestito MUI claro
                }}
              >
                <TableCell>
                  <EstadoReserva reservaId={reserva.id} />
                </TableCell>
                <TableCell>{reserva.id}</TableCell>
                <TableCell>{reserva.createdAt}</TableCell>
                <TableCell>{`${reserva.responsable_nombre} ${reserva.responsable_apellido}`}</TableCell>
                <TableCell>{reserva.fecha_entrada}</TableCell>
                <TableCell>{reserva.fecha_salida}</TableCell>
                <TableCell>
                  {reserva.carpeta_aceptacion ? (
                    <Button
                      variant="outlined"
                      color="primary"
                      href={reserva.carpeta_aceptacion}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Ver archivo
                    </Button>
                  ) : (
                    "Sin archivo"
                  )}
                </TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenDeleteDialog(reserva);
                    }}
                  >
                    Eliminar
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal de Detalles */}
      <DetalleReservaModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        reserva={selectedReserva}
      />

      {/* Diálogo de Confirmación de Eliminación */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que deseas eliminar la reserva con ID{" "}
            <strong>{reservaToDelete?.id}</strong>? Esta acción no se puede
            deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancelar</Button>
          <Button
            onClick={handleDeleteReserva}
            color="error"
            variant="contained"
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ReservasVisualizador;
